import { isDateBefore, isDateValid } from '@utils/dates/date.utils';

// Number of years before exchangeStartYear to include when historical data is enabled
export const CONFIGURED_HISTORICAL_YEARS = 4;

/**
 * Returns an array of years for the dropdown based on server date, OEP dates, and historical data settings
 */
export const getYearDropDownOptions = (
    serverDate: string,
    oepStartDate: string,
    coverageYear: number | string,
    exchangeStartYear: number | string,
    showHistoricalData: boolean = false
): number[] => {
    let totalYears = 0;

    switch (true) {
        case !isDateValid(serverDate, 'YYYY-MM-DD') || !isDateValid(oepStartDate, 'YYYY-MM-DD'):
            break;
        case typeof coverageYear !== 'number' || typeof exchangeStartYear !== 'number':
            break;
        case isDateBefore(serverDate, oepStartDate):
            totalYears = (coverageYear as number) - (exchangeStartYear as number);
            break;
        default:
            totalYears = (coverageYear as number) - (exchangeStartYear as number) + 1;
    }

    const regularYears = Array.from({ length: totalYears }, (_, i) => i + (exchangeStartYear as number));

    if (showHistoricalData) {
        const historicalYears = getHistoricalYearDropDownOptions(exchangeStartYear as number);
        return [...historicalYears, ...regularYears];
    }

    return regularYears;
};

/**
 * Returns an array of historical years from (exchangeStartYear - CONFIGURED_HISTORICAL_YEARS) to (exchangeStartYear - 1)
 * @param exchangeStartYear - The exchange start year
 * @returns Array of historical years in ascending order
 */
export const getHistoricalYearDropDownOptions = (exchangeStartYear: number | string | null | undefined): number[] => {
    if (typeof exchangeStartYear !== 'number') {
        return [];
    }

    const startYear = exchangeStartYear - CONFIGURED_HISTORICAL_YEARS;
    return Array.from({ length: CONFIGURED_HISTORICAL_YEARS }, (_, i) => startYear + i);
};

/**
 * Checks if a given year is a historical year (before exchangeStartYear)
 * @param selectedYear - The year to check
 * @param exchangeStartYear - The exchange start year
 * @returns True if the year is historical, false otherwise (including for NaN, Infinity, or non-numeric values)
 */
export const isHistoricalYear = (
    selectedYear: number | string | null | undefined,
    exchangeStartYear: number | string | null | undefined
): boolean => {
    const year = typeof selectedYear === 'string' ? Number(selectedYear) : selectedYear;
    const exchangeYear = typeof exchangeStartYear === 'string' ? Number(exchangeStartYear) : exchangeStartYear;
    return Number.isFinite(year) && 
           Number.isFinite(exchangeYear) && 
           (year as number) > 0 &&
           (year as number) < (exchangeYear as number);
};
